import { isArray } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UseFieldArrayReturn,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useSWR, { mutate } from 'swr';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { CostCenter } from '~/data/costCenters';
import {
  EmpPosition,
  EmploymentData,
  fetchEmployee,
  useEmployee,
} from '~/data/employee';
import { useEmployeeEmploymentData } from '~/data/employee/useEmployeeEmploymentData';
import { useAllEmployees } from '~/data/employees';
import {
  EmploymentDetailType,
  useEmploymentDetails,
} from '~/data/employment/useEmploymentDetails';
import { useAllEnums } from '~/data/enums';
import { useMySpecificUnits } from '~/data/orgunits/useMySpecificUnits';
import { useOrgTree } from '~/data/orgunits/useOrgTree';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
  ProposalProps,
  ProposalStatus,
} from '~/data/proposals';
import {
  EmploymentProposalsDataAndConfig,
  filterAndSortEmploymentProposals,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import {
  EmploymentFormValues,
  useEmployment,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/useEmployment';
import {
  mapEmpPositionPostData,
  mapEmpPositionProposalPostData,
  submitEmpPositionWorkflow,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data';
import { ScheduleEmploymentFormValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import {
  mapEmploymentDetailsPostData,
  mapEmploymentProposalDetailsPostData,
  submitEmplDetailsWorkflow,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/data';
import { ScheduleEmploymentDetailFormValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHR, isRoleManager } from '~/services/Permissions';
import { AppDispatch } from '~/store';
import { browserLogError } from '~/utils/helper';

import {
  businessUnitChange,
  contractNumberChange,
  deleteEmployment,
  employeeNumberChange,
  getContracts,
  getPendingData,
  mapDefaultData,
  scheduleChanges,
  updateContracts,
} from './data';

export type Contracts = {
  employeeId: number;
  isDefault: boolean;
  position: string;
  uid: string;
  unit: number;
};

export const PENDING_SCHEDULE_CHANGES_SWR_KEY = 'empId';

export type PendingScheduleChanges = {
  empPosition: EmpPosition;
  employment: EmploymentData;
  employmentDetails: EmploymentDetailType;
};

export type Restrict = {
  manager: boolean;
  hr: boolean;
  inMyHRUnit: boolean;
  notAllowEmpTermDate: boolean;
  managerCantRehire: boolean;
};

export type AdditionalCostCentersState = {
  [key: number]: CostCenter[];
};

export type UseScheduleOutput = {
  scheduleFormMethods: UseFormReturn<Form>;
  additionalCostCenters: AdditionalCostCentersState;
  loading: boolean;
  restrictField: Restrict;
  pendingData: PendingScheduleChanges[] | undefined;
  submitting: boolean;
  //@ts-ignore
  scheduleFieldArrayMethods: UseFieldArrayReturn<Form, 'schedule', 'id'>;
  defaultResponsibleHrId?: number;
  allResponsibleHRIds: number[];

  setDeletedEmployments: React.Dispatch<React.SetStateAction<number[]>>;
  setAdditionalCostCenters: React.Dispatch<
    React.SetStateAction<AdditionalCostCentersState>
  >;
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  onSave: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDialogClose: () => void;
  resetFormAfterProposalDecision: ({
    emplDetPropData,
    empPositionPropData,
  }: {
    emplDetPropData?: EmploymentDetailsProposalProps;
    empPositionPropData?: EmploymentPositionProposalProps;
  }) => void;
  hasDataIssue: boolean;
};
export type BusinessUnitData = { [key: `fEmpBusinessUnit${number}`]: string };

export type ScheduleFormMetaProposalData = {
  proposalId?: string;
  proposalStatus?: string;
};

export type ScheduleForm = {
  empPosition?: ScheduleEmploymentFormValues;
  employment?: EmploymentFormValues;
  employmentDetails?: ScheduleEmploymentDetailFormValues;
  fEmpContractNumber?: string;
  mainContract?: boolean;
  fEmpPersonNumber?: string;
  meta?: {
    positionProposal: ScheduleFormMetaProposalData;
    detailsProposal: ScheduleFormMetaProposalData;
  };
} & BusinessUnitData;

export type Form = {
  schedule: ScheduleForm[];
};

export type ChangedStartDateState = {
  emplDetailsStartDate: boolean;
  empPositionStartDate: boolean;
};

const filterAndSortValidProps = (defaultProp: ProposalProps[]) =>
  defaultProp
    .filter((prop) => prop.proposalStatus !== ProposalStatus.FAILED)
    .sort((prop2, prop1) => moment(prop1.createdTime).diff(prop2.createdTime));

export const useScheduleChanges = (
  empId: number,
  handleCloseDialog: () => void,
  proposalsData: EmploymentProposalsDataAndConfig,
): UseScheduleOutput => {
  // General utils
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // General configs & data
  const { applicationConfig } = useApplicationConfig();
  const managerCanTerminate =
    !!applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrTerminateEmployee;
  const managerCanRehire =
    !!applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrRehireEmployee;

  const { orgTree } = useOrgTree();
  const { allEnums } = useAllEnums();

  // Logged user
  const loggedInUserId = useLoggedInEmployeeId();
  const roles = useLoggedInUserRoles();
  const isHRUser = isRoleHR(roles);
  const isMGR = isRoleManager(roles);

  //Change values when changing org unit, have to be discussed
  const {
    allResponsibleHRIds,
    defaultResponsibleHrId,
    isLoading: HRRespLoading,
  } = useResponsibleHRSelect(empId);
  const [deletedEmployments, setDeletedEmployments] = useState<number[]>([]);
  const [changedStartDate, setChangedStartDate] =
    useState<ChangedStartDateState>({
      emplDetailsStartDate: false,
      empPositionStartDate: false,
    });

  // States
  const [submitting, setSubmitting] = useState(false);

  // Employee & employment data
  const {
    employee: { empBasicData, empPosition, fEmpBusinessUnits },
  } = useEmployee();
  const { getEmployeeById, isLoading: employeesLoading } = useAllEmployees();
  const { employeeInfo, fEmpEmplId } = useMemo(() => {
    const employeeInfo = getEmployeeById(empId);

    return {
      employeeInfo,
      fEmpEmplId: employeeInfo?.fEmpEmplId ?? -1,
    };
  }, [getEmployeeById, empId]);

  const { data: empContracts, mutate: mutateContracts } = useSWR<{
    contracts: Contracts[];
    profile: { firstName: string; lastName: string };
  }>(
    ['contracts', empBasicData.fEmpProfileId],
    () => getContracts(empBasicData.fEmpProfileId),
    { revalidateOnFocus: false },
  );
  const { mapEmploymentPostData } = useEmployment();
  const { data: employmentsDetail } = useEmploymentDetails(
    empId,
    employeeInfo?.fEmpEmplId,
  );

  const { data: pendingData, isLoading: isLoadingPendingData } = useSWR<
    PendingScheduleChanges[]
  >([PENDING_SCHEDULE_CHANGES_SWR_KEY, empId], () => getPendingData(empId), {
    revalidateOnFocus: false,
  });

  const { employeeEmploymentData } = useEmployeeEmploymentData();
  const employmentData = useMemo(
    () => employeeEmploymentData[0],
    [employeeEmploymentData],
  );

  // Proposals
  const {
    details: {
      proposals: defaultDetailsProposals,
      isLoading: detailsPropLoading,
      mutate: mutateDetailsPos,
    },
    positions: {
      proposals: defaultEmpPositionProposals,
      isLoading: empPosPropsLoading,
      mutate: mutateInternalPos,
    },
    config: { proposalsEnabled },
  } = proposalsData;
  const employmentDetailsProposals = filterAndSortEmploymentProposals(
    defaultDetailsProposals,
  );

  const empPositionProposals = filterAndSortEmploymentProposals(
    defaultEmpPositionProposals,
  );

  const hasLoadedProposals = proposalsEnabled
    ? !empPosPropsLoading && !detailsPropLoading
    : true;
  const hasLoaded = hasLoadedProposals && !isLoadingPendingData;
  const loadingStatesChanged = useHasChanged(hasLoaded);
  const doFormReset = loadingStatesChanged && hasLoaded;

  const getInitialFieldValues = useCallback(
    ({
      emplDetPropData,
      newPendingData,
      empPositionPropData,
    }: {
      empPositionPropData?: EmploymentPositionProposalProps;
      emplDetPropData?: EmploymentDetailsProposalProps;
      newPendingData?: PendingScheduleChanges[];
    }) => {
      const hasEmploymentDetails = employmentsDetail.length > 0;

      if (!hasEmploymentDetails) {
        browserLogError(
          `Scheduled Employment Changes: Employee doesn't have Employment Details`,
        );

        return [];
      }

      return mapDefaultData({
        empCurrentData: empPosition,
        employmentCurrentData: employmentData,
        employmentCurrentDetail: employmentsDetail[0],
        fEmpBusinessUnits,
        orgTree,
        allEnums,
        contractNumber: empBasicData.fEmpContractNumber,
        personNumber: empBasicData.fEmpPersonNumber,
        pendingData: newPendingData ? newPendingData : pendingData!,
        mainContract:
          empContracts?.contracts.find((c) => c.employeeId === empId)
            ?.isDefault || false,
        employmentDetailsProposal: proposalsEnabled
          ? emplDetPropData || employmentDetailsProposals[0]
          : undefined,
        empPositionProposal: proposalsEnabled
          ? empPositionPropData || empPositionProposals[0]
          : undefined,
        defaultResponsibleHrId:
          isHRUser && allResponsibleHRIds.includes(loggedInUserId)
            ? loggedInUserId
            : defaultResponsibleHrId,
      });
    },
    [
      allEnums,
      allResponsibleHRIds,
      defaultResponsibleHrId,
      empBasicData.fEmpContractNumber,
      empBasicData.fEmpPersonNumber,
      empContracts?.contracts,
      empId,
      empPosition,
      empPositionProposals,
      employmentData,
      employmentDetailsProposals,
      employmentsDetail,
      fEmpBusinessUnits,
      isHRUser,
      loggedInUserId,
      proposalsEnabled,
      orgTree,
      pendingData,
    ],
  );

  const scheduleFormMethods = useForm<Form>({
    defaultValues: {
      //@ts-ignore
      //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      schedule: getInitialFieldValues({}),
    },
  });

  const [additionalCostCenters, setAdditionalCostCenters] =
    useState<AdditionalCostCentersState>({});

  const {
    handleSubmit,
    control,
    formState: { dirtyFields },
    reset,
  } = scheduleFormMethods;

  const handleFormReset = useCallback(() => {
    reset({ schedule: getInitialFieldValues({}) });
  }, [reset, getInitialFieldValues]);

  useEffect(() => {
    if (doFormReset) {
      handleFormReset();
    }
  }, [doFormReset, handleFormReset]);

  //@ts-ignore
  const scheduleFieldArrayMethods = useFieldArray({
    control,
    name: 'schedule',
  });

  const { aMyHRUnits } = useMySpecificUnits();
  const restrictField = useMemo(() => {
    const inMyHRUnit = !!employeeInfo
      ? aMyHRUnits.includes(employeeInfo.fEmpUnit)
      : false;

    return {
      manager: isMGR,
      hr: isHRUser,
      inMyHRUnit: inMyHRUnit,
      notAllowEmpTermDate:
        (!isHRUser && isMGR && !managerCanTerminate) ||
        (isHRUser && !inMyHRUnit) ||
        (!isHRUser && !isMGR),
      managerCantRehire:
        (!isHRUser && isMGR && !managerCanRehire) ||
        (isHRUser && !inMyHRUnit) ||
        (!isHRUser && !isMGR),
    };
  }, [
    isMGR,
    isHRUser,
    aMyHRUnits,
    employeeInfo,
    managerCanRehire,
    managerCanTerminate,
  ]);

  const checkIfAnyFieldDirty = useCallback(
    (fields: Object, exception?: string): boolean => {
      const isException = (key: string) =>
        !!exception && key.includes(exception);
      const processObject = (obj: Object): string[] => {
        // Resolve Key Value pair from array index & value or object entries
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const entries: [string, any][] = isArray(obj)
          ? obj.map((value, index) => [`${index}`, value])
          : Object.entries(obj);

        // Reduce to array of dirty field paths
        // Count only properties and sub-properties with a boolean value as true
        return entries.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc: string[], entry: [string, any]): string[] => {
            const [key, value] = entry;
            if (isException(key)) {
              return acc;
            }

            switch (typeof value) {
              case 'boolean':
                return value ? [...acc, key] : acc;
              case 'object':
                const processed = processObject(value)
                  .map((k) => `${key}.${k}`)
                  .filter((k) => !isException(k));
                return [...acc, ...processed];
              default:
                return acc;
            }
          },
          [],
        );
      };

      const dirtyFieldPaths = processObject(fields);

      return !!dirtyFieldPaths.length;
    },
    [],
  );

  const onDialogClose = useCallback(() => {
    setAdditionalCostCenters({});
    setDeletedEmployments([]);
    dispatch(fetchEmployee({ empId }));
    handleCloseDialog();
  }, [dispatch, empId, handleCloseDialog]);

  const deleteEmplContractNumEmpNumberChanges = useCallback(
    //INFO: these fields have to be submitted separatly, and they will appear only in the current empl form
    async (submitData: Form) => {
      await Promise.all(
        deletedEmployments.map(
          async (emplId) => await deleteEmployment(empId, emplId),
        ),
      );
      if (
        !!dirtyFields.schedule?.[0]?.fEmpContractNumber &&
        submitData.schedule[0].fEmpContractNumber
      ) {
        await contractNumberChange(
          submitData.schedule[0].fEmpContractNumber,
          empId,
        );
      }

      const mainContract = empContracts?.contracts.find(
        (c) => c.employeeId === empId,
      );
      const notMainContractMainContractChanged =
        mainContract &&
        mainContract.isDefault !== submitData.schedule[0].mainContract &&
        !!dirtyFields.schedule?.[0]?.mainContract;
      if (notMainContractMainContractChanged) {
        await updateContracts(mainContract.uid, empBasicData.fEmpProfileId);
        mutateContracts();
      }

      if (
        !!dirtyFields.schedule?.[0]?.fEmpPersonNumber &&
        submitData.schedule[0].fEmpPersonNumber
      ) {
        await employeeNumberChange(
          submitData.schedule[0].fEmpPersonNumber,
          empId,
        ).then((r) => {
          if (r.error) {
            enqueueSnackbar(`1. ${t('fEmploymentGroup')}: ${t(r.error)}`, {
              variant: 'error',
            });
            setSubmitting(false);
          }
        });
      }

      const businessFields = dirtyFields.schedule?.[0]
        ? Object.keys(dirtyFields.schedule?.[0]).filter((dirtyKey) =>
            dirtyKey.includes('fEmpBusinessUnit'),
          )
        : undefined;
      if (businessFields) {
        const businessFieldsPostData = Object.entries(submitData.schedule[0])
          .filter(([businessFieldKey, _businessFieldValue]) =>
            businessFieldKey.includes('fEmpBusinessUnit'),
          )
          .map(([businessFieldKey, businessFieldValue]) => ({
            treeId: parseInt(businessFieldKey.split('fEmpBusinessUnit')[1]),
            unitId: parseInt(businessFieldValue as string) || -1,
          }));
        await businessUnitChange(businessFieldsPostData, empId);
      }
    },
    [
      deletedEmployments,
      dirtyFields.schedule,
      empBasicData.fEmpProfileId,
      empContracts?.contracts,
      empId,
      enqueueSnackbar,
      mutateContracts,
      t,
    ],
  );

  const currentEmploymentSubmition = useCallback(
    async (submitData) => {
      try {
        if (typeof fEmpEmplId === 'undefined' || fEmpEmplId < 1) {
          enqueueSnackbar(t('laGeneralError'), {
            variant: 'error',
          });
          throw Error(`Invalid Employment Id: ${fEmpEmplId}`);
        }

        if (
          !!dirtyFields.schedule?.[0]?.employment &&
          submitData.employment &&
          checkIfAnyFieldDirty(dirtyFields?.schedule?.[0]?.employment || {})
        ) {
          const payload = {
            employment: mapEmploymentPostData(submitData.employment, false),
          };
          await scheduleChanges(empId, payload, fEmpEmplId).then((r) => {
            if (r.error) {
              enqueueSnackbar(`1. ${t('fEmploymentGroup')}: ${t(r.error)}`, {
                variant: 'error',
              });
              throw Error(r.error);
            }
          });
        }

        const canCreateNewProposal = (
          formMetaProposalData: ScheduleFormMetaProposalData,
        ) => {
          const { proposalStatus } = formMetaProposalData;
          const isPending =
            !!proposalStatus &&
            [ProposalStatus.IN_PROGRESS].includes(
              proposalStatus as ProposalStatus,
            );

          return !isPending;
        };

        if (
          !!dirtyFields.schedule?.[0]?.employmentDetails &&
          submitData.employmentDetails &&
          checkIfAnyFieldDirty(
            dirtyFields?.schedule?.[0]?.employmentDetails || {},
            'StartDate',
          ) &&
          canCreateNewProposal(submitData.meta.detailsProposal)
        ) {
          const payload = mapEmploymentProposalDetailsPostData(
            submitData.employmentDetails,
            roles,
          );
          await submitEmplDetailsWorkflow(empId, fEmpEmplId, payload).then(
            (r) => {
              if (r.error) {
                enqueueSnackbar(`1. ${t('fEmploymentGroup')}: ${t(r.error)}`, {
                  variant: 'error',
                });
                throw Error(r.error);
              }
            },
          );
        }
        if (
          !!dirtyFields.schedule?.[0]?.empPosition &&
          submitData.empPosition &&
          checkIfAnyFieldDirty(
            dirtyFields?.schedule?.[0]?.empPosition || {},
            'StartDate',
          ) &&
          canCreateNewProposal(submitData.meta.positionProposal)
        ) {
          const payload = mapEmpPositionProposalPostData(
            submitData.empPosition,
            additionalCostCenters?.[0] || [],
            empId.toString(),
            roles,
            fEmpEmplId,
          );

          await submitEmpPositionWorkflow(empId, payload).then((r) => {
            if (r.error) {
              enqueueSnackbar(`1. ${t('fEmploymentGroup')}: ${t(r.error)}`, {
                variant: 'error',
              });
              throw Error(r.error);
            }
          });
        }
      } catch (e) {
        return { error: e };
      }
    },
    [
      additionalCostCenters,
      checkIfAnyFieldDirty,
      dirtyFields,
      empId,
      enqueueSnackbar,
      fEmpEmplId,
      mapEmploymentPostData,
      roles,
      t,
    ],
  );

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit(async (submitData) => {
      try {
        setSubmitting(true);
        deleteEmplContractNumEmpNumberChanges(submitData);
        //INFO: if workflow configs are set and schedule should use proposal, first we submit the current employment as proposal
        if (proposalsEnabled) {
          await currentEmploymentSubmition(submitData.schedule[0]).then((r) => {
            if (r && r.error) {
              throw Error(
                [
                  'Something went wrong while submitting scheduling proposal.',
                  r.error,
                ].join('\n'),
              );
            }
          });
        }
        submitData.schedule
          //INFO: if current employment is submitted as proposal we take it out from the array
          .slice(proposalsEnabled ? 1 : 0)
          .map((data, defaultIndex) => {
            //INFO: if current employment is taken out, every field is moved up in the array, so we have to set the right index
            const index = proposalsEnabled ? defaultIndex + 1 : defaultIndex;
            const isCurrentEmployment = index === 0;
            const employmentId = data?.employmentDetails?.fEmplId || undefined;

            return {
              employmentId: isCurrentEmployment
                ? fEmpEmplId
                : employmentId
                ? employmentId
                : undefined,
              payload: {
                ...(!!dirtyFields.schedule?.[index]?.empPosition &&
                  data.empPosition &&
                  (checkIfAnyFieldDirty(
                    dirtyFields?.schedule?.[index]?.empPosition || {},
                    'StartDate',
                  ) ||
                    changedStartDate.empPositionStartDate) && {
                    empPosition: mapEmpPositionPostData(
                      data.empPosition,
                      additionalCostCenters?.[index] || [],
                      empId.toString(),
                      isCurrentEmployment,
                      pendingData &&
                        pendingData.length &&
                        pendingData?.[index]?.empPosition
                        ? pendingData[index].empPosition.fInternalPositionId
                        : undefined,
                    ),
                  }),
                ...(!!dirtyFields.schedule?.[index]?.employmentDetails &&
                  data.employmentDetails &&
                  (checkIfAnyFieldDirty(
                    dirtyFields?.schedule?.[index]?.employmentDetails || {},
                    'StartDate',
                  ) ||
                    changedStartDate.emplDetailsStartDate) && {
                    employmentDetails: mapEmploymentDetailsPostData(
                      data.employmentDetails,
                      employmentId ? employmentId : undefined,
                      pendingData &&
                        pendingData.length &&
                        pendingData?.[index]?.employmentDetails?.fEmplUid
                        ? (pendingData[index].employmentDetails
                            .fEmplUid as string)
                        : isCurrentEmployment
                        ? 'ACTIVE'
                        : 'PENDING',
                    ),
                  }),
                ...(!!dirtyFields.schedule?.[index]?.employment &&
                  data.employment &&
                  checkIfAnyFieldDirty(
                    dirtyFields?.schedule?.[index]?.employment || {},
                  ) && {
                    employment: mapEmploymentPostData(
                      data.employment,
                      !isCurrentEmployment,
                    ),
                  }),
              },
            };
          })
          //INFO: because a new employment can't be created without ending the previous one the api calls have to submit after the previous completed,
          // so first we map the data what has to be submitted, and after we iterate over the promises and waiting each other
          .reduce((previousPromise, nextPostData, defaultIndex) => {
            return previousPromise.then(async () => {
              //INFO: if current empl is taken out, every field is moved up in the array, so we have to set the right index
              const index = proposalsEnabled ? defaultIndex + 1 : defaultIndex;
              if (Object.keys(nextPostData.payload).length > 0) {
                return await scheduleChanges(
                  empId,
                  nextPostData.payload,
                  nextPostData.employmentId,
                ).then((r) => {
                  if (r.error) {
                    enqueueSnackbar(
                      `${index + 1}. ${t('fEmploymentGroup')}: ${t(r.error)}`,
                      {
                        variant: 'error',
                      },
                    );
                  }
                });
              }
            });
          }, Promise.resolve())
          .then(() => {
            setSubmitting(false);
            enqueueSnackbar(t('message_success_approval'), {
              variant: 'success',
            });
            mutate([PENDING_SCHEDULE_CHANGES_SWR_KEY, empId], () =>
              getPendingData(empId).then((newPendingData) => {
                mutateInternalPos().then((defaultNewEmpPositionProposals) => {
                  mutateDetailsPos().then(() => {
                    const empPositionPropData = defaultNewEmpPositionProposals
                      ? (
                          filterAndSortValidProps(
                            defaultNewEmpPositionProposals.data as unknown as ProposalProps[],
                          ) as EmploymentPositionProposalProps[]
                        )[0]
                      : undefined;

                    const emplDetPropData = defaultNewEmpPositionProposals
                      ? (
                          filterAndSortValidProps(
                            defaultNewEmpPositionProposals.data as unknown as ProposalProps[],
                          ) as EmploymentDetailsProposalProps[]
                        )[0]
                      : undefined;
                    reset({
                      schedule: getInitialFieldValues({
                        newPendingData,
                        emplDetPropData,
                        empPositionPropData,
                      }),
                    });
                  });
                });
              }),
            );
            onDialogClose();
          })
          .catch((_err) => {
            setSubmitting(false);
            mutate([PENDING_SCHEDULE_CHANGES_SWR_KEY, empId], () =>
              getPendingData(empId),
            );
            setDeletedEmployments([]);
            dispatch(fetchEmployee({ empId }));
          });
      } catch (e) {
        setSubmitting(false);
        setAdditionalCostCenters({});
        console.error(e);
      }
    });
  }, [
    additionalCostCenters,
    changedStartDate.empPositionStartDate,
    changedStartDate.emplDetailsStartDate,
    checkIfAnyFieldDirty,
    currentEmploymentSubmition,
    deleteEmplContractNumEmpNumberChanges,
    dirtyFields.schedule,
    dispatch,
    empId,
    enqueueSnackbar,
    fEmpEmplId,
    getInitialFieldValues,
    handleSubmit,
    mapEmploymentPostData,
    mutateDetailsPos,
    mutateInternalPos,
    proposalsEnabled,
    onDialogClose,
    pendingData,
    reset,
    t,
  ]);

  const resetFormAfterProposalDecision = useCallback(
    ({
      emplDetPropData,
      empPositionPropData,
    }: {
      emplDetPropData?: EmploymentDetailsProposalProps;
      empPositionPropData?: EmploymentPositionProposalProps;
    }) => {
      reset({
        schedule: getInitialFieldValues({
          emplDetPropData,
          empPositionPropData,
        }),
      });
    },
    [getInitialFieldValues, reset],
  );

  const onSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  const hasDataIssue = useMemo(() => {
    const hasEmploymentDetails = employmentsDetail.length > 0;

    return !hasEmploymentDetails;
  }, [employmentsDetail]);

  return {
    scheduleFormMethods,
    onSave,
    submitting,
    loading: employeesLoading || HRRespLoading,
    additionalCostCenters,
    setAdditionalCostCenters,
    pendingData,
    restrictField,
    scheduleFieldArrayMethods,
    setDeletedEmployments,
    onDialogClose,
    setChangedStartDate,
    allResponsibleHRIds,
    resetFormAfterProposalDecision,
    hasDataIssue,
  };
};
